import {Outlist} from '@/Agent/Quotes/InstantAppModal';
import {mapParamStoreValueToAgenium} from '@/Agent/Quotes/InstantAppModalPages/utils';
import {EMPLOYER_OPTIONAL_OCCUPATIONS} from '@/common/constants';
import _ from 'lodash';
import {IS_PREPROD, IS_PRODUCTION} from '../../constants';
import {Approval} from '../mobx/ParameterStore/Approval';
import {fieldOptions} from '../mobx/ParameterStore/ApprovalXref';
import {ParameterStore} from '../mobx/ParameterStore/ParameterStore';
import RootStore from '../mobx/RootStore';

// 202e1aeb-1522-4c86-af38-163bcf7c0016: manual underwriting page

export const messagesToSkip: string[] = [
    'bdcc023f-98d8-4ee2-b57c-abb1f36b80a1', // Welcome amplify agent
    '5f7fe02c-f813-4c20-b131-bc4617d1c389', // refer data
    'bc7ec1e1-5e64-499e-8a50-eae32d8ae288', // Final underwriting decision
    'a44f90ab-3c65-410f-a0ef-30a79d4ddc2e', // Rate api technical data structure
    'd6886c80-6370-424b-a176-549f268c7e03', // quote display
    '202e1aeb-1522-4c86-af38-163bcf7c0016', // Manual Underwriting page
    '61834805-2c79-4984-8481-8de5417ecda0', // New Flat Extra Case_UWDecision_FlatExtraFinal
];

export const questionsToSkip: string[] = [
    'Case_OtherInsurance_CompanyAddress',
    'Case_OtherInsurance_PolicyNumber',
    'Case_OtherInsurance_IssuedYear',
    'Case_OtherInsurance_Reason',
    'Case_OtherInsurance_CompanyAddress_1',
    'Case_OtherInsurance_PolicyNumber_1',
    'Case_OtherInsurance_IssuedYear_1',
    'Case_OtherInsurance_Reason_1',
    'Case_OtherInsurance_CompanyAddress_2',
    'Case_OtherInsurance_PolicyNumber_2',
    'Case_OtherInsurance_IssuedYear_2',
    'Case_OtherInsurance_Reason_2',
];

export const alwaysBootstrap = [
    {key: 'Case_UWDecision_Ready', value: 'Yes'}, //This should be removed for customer facing.
];

const prosperTest = (prefillMode: string, parameterStore: ParameterStore) => {
    if (
        !IS_PRODUCTION &&
        !IS_PREPROD &&
        parameterStore.approval.notes.value.startsWith('ProsperTest') &&
        parameterStore.approval.notes.value.includes(prefillMode)
    ) {
        return true;
    }
    return false;
};

export const skipEmptyBootstrapValue = (key: string) => {
    const skipEmptyKeys = ['Case_Applicant_MiddleName', 'Case_Applicant_AddressLine2'];
    return skipEmptyKeys.includes(key);
};

export const createStaticBootstrap = (parameterStore: ParameterStore) => {
    return [
        {key: 'Case_Applicant_ProductType', value: 'Indexed Life Product'},
        {key: 'Case_Applicant_PlanType', value: 'Indexed Universal Life'},

        {key: 'Case_Applicant_AdditionalSPPercent', value: '100'},
        {key: 'Case_Applicant_AdditionalFixedPercent', value: '0'},
        {key: 'Case_Applicant_Coverage', value: 'Personal'},

        {
            key: 'Case_Applicant_HealthCareAuthorization',
            value: 'Check here to acknowledge that you have carefully reviewed and fully understand the above statement.',
        },

        {key: 'Case_AgentReport_AgentAssisted', value: 'Yes'},
        {key: 'Case_UWDecision_EvaluateDecision', value: 'IUL'},
        //...alwaysBootstrap,

        // only on non-prod environments
        // to by-pass beneficaries issue
        ...(prosperTest('Beneficiary1', parameterStore)
            ? [
                  {key: 'Case_BeneDetails_Percentage', value: '100'},
                  {key: 'Case_BeneDetails_FirstName', value: 'K1'},
                  {key: 'Case_BeneDetails_MiddleName', value: 'M1'},
                  {key: 'Case_BeneDetails_LastName', value: 'C1'},
                  {key: 'Case_BeneDetails_AddressLine1', value: 'A1'},
                  {key: 'Case_BeneDetails_AddressLine2', value: 'A2'},
                  {key: 'Case_BeneDetails_City', value: 'C1'},
                  {key: 'Case_BeneDetails_SSN', value: '111223333'},
                  {key: 'Case_BeneDetails_BeneficiaryType', value: 'Individual'},
                  {key: 'Case_BeneDetails_DOB', value: '01/01/2000'},
                  {key: 'Case_BeneDetails_State', value: 'CA'},
                  {key: 'Case_BeneDetails_PostalCode', value: '11111'},
                  {key: 'Case_BeneDetails_Relationship', value: 'Child'},
                  {key: 'Case_BeneDetails_RelationshipQ1', value: 'Spouse'},
              ]
            : []),
        // Approved Data
        ...(prosperTest('Approved', parameterStore)
            ? [
                  {key: 'Case_Applicant_BirthCountry', value: 'United States of America'},
                  {key: 'Case_Applicant_BirthState', value: 'CA'},
                  {key: 'Case_Coverage_Personal', value: 'Income Replacement'},
                  {key: 'Case_Applicant_Occupation', value: 'Accountant'},
                  {key: 'Case_Applicant_PayorPISame', value: 'Yes'},
                  {key: 'Case_Applicant_OwnerPISame', value: 'Yes'},
                  {key: 'Case_Tobacco_1', value: 'Never'},
                  {key: 'Case_Tobacco_2', value: 'Never'},
                  {key: 'Case_Lifestyle_1', value: 'No'},
                  {key: 'Case_Lifestyle_2', value: 'No'},
                  {key: 'Case_Lifestyle_3', value: 'None of the above'},
                  {key: 'Case_Lifestyle_4', value: 'No'},
                  {key: 'Case_Lifestyle_5', value: 'No'},
                  {key: 'Case_Lifestyle_6', value: 'Never'},
                  {key: 'Case_Lifestyle_6DrinksAvgWeek', value: '0'},
                  {key: 'Case_Lifestyle_7', value: 'Never'},
                  {key: 'Case_Lifestyle_8', value: 'No'},
                  {key: 'Case_NonMedical_1', value: 'No'},
                  {key: 'Case_NonMedical_2Avocation', value: 'None of the above'},
                  {key: 'Case_Medical_HighLevelPointerGroups', value: 'None of the above'},
                  {key: 'Case_Medical_2', value: 'No'},
                  {key: 'Case_Medical_3', value: 'No'},
                  {key: 'Case_Medical_4', value: 'No'},
                  {key: 'Case_FamilyHistory_Diseases', value: 'No'},
                  {key: 'Case_Applicant_PendingInsurance', value: 'No'},
                  {key: 'Case_Applicant_ExInsurance', value: 'No'},
                  {key: 'Case_Applicant_AppliedInsuranceDeclined', value: 'No'},
                  {key: 'Case_Applicant_PremiumSource', value: 'Personal Income'},
                  {key: 'Case_BeneDetails_ContBene', value: 'No'},
                  {key: 'Case_Applicant_DisabilityBenefits', value: 'No'},
                  {key: 'Case_Applicant_Bankruptcy', value: 'No'},
              ]
            : []),
        // Decline 1 Data
        ...(prosperTest('DACLAN_001', parameterStore)
            ? [
                  {key: 'Case_Applicant_BirthCountry', value: 'United States of America'},
                  {key: 'Case_Applicant_BirthState', value: 'CA'},
                  {key: 'Case_Coverage_Personal', value: 'Income Replacement'},
                  {key: 'Case_Applicant_Occupation', value: 'Accountant'},
                  {key: 'Case_Applicant_PayorPISame', value: 'Yes'},
                  {key: 'Case_Applicant_OwnerPISame', value: 'Yes'},
                  {key: 'Case_Tobacco_1', value: 'Never'},
                  {key: 'Case_Tobacco_2', value: 'Never'},
                  {key: 'Case_Lifestyle_1', value: 'No'},
                  {key: 'Case_Lifestyle_2', value: 'No'},
                  {key: 'Case_Lifestyle_3', value: 'None of the above'},
                  {key: 'Case_Lifestyle_4', value: 'No'},
                  {key: 'Case_Lifestyle_5', value: 'No'},
                  {key: 'Case_Lifestyle_6', value: 'Never'},
                  {key: 'Case_Lifestyle_6DrinksAvgWeek', value: '0'},
                  {key: 'Case_Lifestyle_7', value: 'Never'},
                  {key: 'Case_Lifestyle_8', value: 'No'},
                  {key: 'Case_NonMedical_1', value: 'No'},
                  {key: 'Case_NonMedical_2Avocation', value: 'None of the above'},
                  {key: 'Case_Medical_HighLevelPointerGroups', value: 'Lymphatic'},
                  {key: 'Case_Medical_1hConditions', value: 'Type II Diabetes'},
                  {key: 'Case_Medical_A1cLevelKnown', value: 'Yes'},
                  {key: 'Case_Medical_A1cLevel', value: '12'},
                  {key: 'Case_Medical_DiagnosedAgeKnown', value: 'No'},
                  {key: 'Case_Medical_Complications', value: 'None of the above'},
                  {key: 'Case_Medical_Type2TreatmentChanges', value: 'No'},
                  {key: 'Case_Medical_2', value: 'No'},
                  {key: 'Case_Medical_3', value: 'No'},
                  {key: 'Case_Medical_4', value: 'No'},
                  {key: 'Case_FamilyHistory_Diseases', value: 'No'},
                  {key: 'Case_Applicant_PendingInsurance', value: 'No'},
                  {key: 'Case_Applicant_ExInsurance', value: 'No'},
                  {key: 'Case_Applicant_AppliedInsuranceDeclined', value: 'No'},
                  {key: 'Case_Applicant_PremiumSource', value: 'Personal Income'},
                  {key: 'Case_BeneDetails_ContBene', value: 'No'},
                  {key: 'Case_Applicant_DisabilityBenefits', value: 'No'},
                  {key: 'Case_Applicant_Bankruptcy', value: 'No'},
                  {key: 'Case_AgentReport_GI1question', value: 'No'},
              ]
            : []),
        // Decline 2 Data
        ...(prosperTest('DACLAN_002', parameterStore)
            ? [
                  {key: 'Case_Applicant_BirthCountry', value: 'United States of America'},
                  {key: 'Case_Applicant_BirthState', value: 'CA'},
                  {key: 'Case_Coverage_Personal', value: 'Income Replacement'},
                  {key: 'Case_Applicant_Occupation', value: 'Accountant'},
                  {key: 'Case_Applicant_PayorPISame', value: 'Yes'},
                  {key: 'Case_Applicant_OwnerPISame', value: 'Yes'},
                  {key: 'Case_Tobacco_1', value: 'Never'},
                  {key: 'Case_Tobacco_2', value: 'Never'},
                  {key: 'Case_Lifestyle_1', value: 'No'},
                  {key: 'Case_Lifestyle_2', value: 'No'},
                  {key: 'Case_Lifestyle_3', value: 'None of the above'},
                  {key: 'Case_Lifestyle_4', value: 'No'},
                  {key: 'Case_Lifestyle_5', value: 'No'},
                  {key: 'Case_Lifestyle_6', value: 'Never'},
                  {key: 'Case_Lifestyle_6DrinksAvgWeek', value: '0'},
                  {key: 'Case_Lifestyle_7', value: 'Never'},
                  {key: 'Case_Lifestyle_8', value: 'No'},
                  {key: 'Case_NonMedical_1', value: 'No'},
                  {
                      key: 'Case_NonMedical_2Avocation',
                      value: 'Hang-gliding Sky Diving Parachuting Ultralight Soaring or Ballooning',
                  },
                  {key: 'Case_NonMedical_2dPlan', value: 'Yes'},
                  {key: 'Case_NonMedical_2dRecordBreakingAttempts', value: 'Yes'},
                  {key: 'Case_NonMedical_2dRecordBreakingDetails', value: 'N/A'},
                  {key: 'Case_NonMedical_2dActivities', value: 'Skydiving Parachuting'},
                  {key: 'Case_NonMedical_2dPast12MonthsJumps', value: '12'},
                  {key: 'Case_NonMedical_2dNext12MonthsJumps', value: '12'},
                  {key: 'Case_NonMedical_2dCertifications', value: 'None'},
                  {key: 'Case_NonMedical_2dExperience', value: '1'},
                  {key: 'Case_NonMedical_2dSkyDivingActivities', value: 'None of the above'},
                  {key: 'Case_Medical_HighLevelPointerGroups', value: 'None of the above'},
                  {key: 'Case_Medical_2', value: 'No'},
                  {key: 'Case_Medical_3', value: 'No'},
                  {key: 'Case_Medical_4', value: 'No'},
                  {key: 'Case_FamilyHistory_Diseases', value: 'No'},
                  {key: 'Case_Applicant_PendingInsurance', value: 'No'},
                  {key: 'Case_Applicant_ExInsurance', value: 'No'},
                  {key: 'Case_Applicant_AppliedInsuranceDeclined', value: 'No'},
                  {key: 'Case_Applicant_PremiumSource', value: 'Personal Income'},
                  {key: 'Case_BeneDetails_ContBene', value: 'No'},
                  {key: 'Case_Applicant_DisabilityBenefits', value: 'No'},
                  {key: 'Case_Applicant_Bankruptcy', value: 'No'},
                  {key: 'Case_AgentReport_GI1question', value: 'No'},
              ]
            : []),
        // Referred
        ...(prosperTest('Referred', parameterStore)
            ? [
                  {key: 'Case_Applicant_BirthCountry', value: 'United States of America'},
                  {key: 'Case_Applicant_BirthState', value: 'CA'},
                  {key: 'Case_Coverage_Personal', value: 'Income Replacement'},
                  {key: 'Case_Applicant_PayorPISame', value: 'Yes'},
                  {key: 'Case_Applicant_OwnerPISame', value: 'Yes'},
                  {key: 'Case_Tobacco_1', value: 'Never'},
                  {key: 'Case_Tobacco_2', value: 'Never'},
                  {key: 'Case_Lifestyle_1', value: 'Yes'},
                  {key: 'Case_Lifestyle_1MilitaryStatus', value: 'Active Duty'},
                  {key: 'Case_Lifestyle_1MilitaryActiveDutyPlans', value: 'Yes'},
                  {key: 'Case_Lifestyle_1MilitaryActiveDutyPlansAddnDetails', value: 'abcde'},
                  {key: 'Case_Lifestyle_1MilitaryActiveDutyDuties', value: 'abcde'},
                  {key: 'Case_Lifestyle_2', value: 'No'},
                  {key: 'Case_Lifestyle_3', value: 'None of the above'},
                  {key: 'Case_Lifestyle_4', value: 'No'},
                  {key: 'Case_Lifestyle_5', value: 'No'},
                  {key: 'Case_Lifestyle_6', value: 'Never'},
                  {key: 'Case_Lifestyle_6DrinksAvgWeek', value: '0'},
                  {key: 'Case_Lifestyle_7', value: 'Never'},
                  {key: 'Case_Lifestyle_8', value: 'No'},
                  {key: 'Case_NonMedical_1', value: 'No'},
                  {key: 'Case_NonMedical_2Avocation', value: 'None of the above'},
                  {key: 'Case_Medical_HighLevelPointerGroups', value: 'None of the above'},
                  {key: 'Case_Medical_2', value: 'No'},
                  {key: 'Case_Medical_3', value: 'No'},
                  {key: 'Case_Medical_4', value: 'No'},
                  {key: 'Case_FamilyHistory_Diseases', value: 'No'},
                  {key: 'Case_Applicant_PendingInsurance', value: 'No'},
                  {key: 'Case_Applicant_ExInsurance', value: 'No'},
                  {key: 'Case_Applicant_AppliedInsuranceDeclined', value: 'No'},
                  {key: 'Case_Applicant_PremiumSource', value: 'Personal Income'},
                  {key: 'Case_BeneDetails_ContBene', value: 'No'},
                  {key: 'Case_Applicant_DisabilityBenefits', value: 'No'},
                  {key: 'Case_Applicant_Bankruptcy', value: 'No'},
                  {key: 'Case_AgentReport_GI1question', value: 'No'},
              ]
            : []),
    ];
};

export const beneRelationshipMoreList = fieldOptions.beneRelationshipAgenium.options.slice(
    fieldOptions.beneRelationshipAgenium.options.indexOf('More') + 1,
);

const createBeneficiaryKeys = (parameterStore: ParameterStore, i: number, cont = false) => {
    const keyPrefix = cont ? 'Contin' : '';
    const apprvPrefix = cont ? 'contB' : 'b';
    return [
        {
            key: `Case_BeneDetails_${keyPrefix}BeneficiaryType${i ? '_' + i : ''}`,
            value: parameterStore.approval[`${apprvPrefix}eneType${i + 1}` as keyof Approval].value,
        },
        {
            key: `Case_BeneDetails_${keyPrefix}FirstName${i ? '_' + i : ''}`,
            value: parameterStore.approval[`${apprvPrefix}eneInfo${i + 1}` as keyof Approval].value.substring(
                0,
                parameterStore.approval[`${apprvPrefix}eneInfo${i + 1}` as keyof Approval].value.indexOf('  '),
            ),
        },
        {
            key: `Case_BeneDetails_${keyPrefix}LastName${i ? '_' + i : ''}`,
            value: parameterStore.approval[`${apprvPrefix}eneInfo${i + 1}` as keyof Approval].value.substring(
                parameterStore.approval[`${apprvPrefix}eneInfo${i + 1}` as keyof Approval].value.indexOf('  ') +
                    (parameterStore.approval[`${apprvPrefix}eneInfo${i + 1}` as keyof Approval].value.includes('  ')
                        ? 2
                        : 1),
            ),
        },
        {
            key: `Case_BeneDetails_${keyPrefix}DOB${i ? '_' + i : ''}`,
            value: `${parameterStore.approval[`${apprvPrefix}eneMonth${i + 1}` as keyof Approval].value}/${parameterStore.approval[`${apprvPrefix}${cont ? 'd' : 'eneD'}ay${i + 1}` as keyof Approval].value}/${parameterStore.approval[`${apprvPrefix}eneYear${i + 1}` as keyof Approval].value}`,
        },
        {
            key: `Case_BeneDetails_${keyPrefix}RelationshipQ1${i ? '_' + i : ''}`,
            value: beneRelationshipMoreList.includes(
                parameterStore.approval[`${apprvPrefix}eneRelationshipAgenium${i + 1}` as keyof Approval]?.value ||
                    'Other',
            )
                ? 'More'
                : parameterStore.approval[`${apprvPrefix}eneRelationshipAgenium${i + 1}` as keyof Approval].value,
        },
        ...(beneRelationshipMoreList.includes(
            parameterStore.approval[`${apprvPrefix}eneRelationshipAgenium${i + 1}` as keyof Approval]?.value || 'Other',
        )
            ? [
                  {
                      key: `Case_BeneDetails_${keyPrefix}RelationshipQ2${i ? '_' + i : ''}`,
                      value:
                          parameterStore.approval[`${apprvPrefix}eneRelationshipAgenium${i + 1}` as keyof Approval]
                              ?.value || 'Other',
                  },
              ]
            : []),
        {
            key: `Case_BeneDetails_${keyPrefix}Percentage${i ? '_' + i : ''}`,
            value: parameterStore.approval[`${apprvPrefix}enePercent${i + 1}` as keyof Approval].value,
        },
        ...((parameterStore.approval[`${apprvPrefix}eneRelationshipAgenium${i + 1}` as keyof Approval]?.value ||
            'Other') === 'Other'
            ? [
                  {
                      key: `Case_BeneDetails_${keyPrefix}Other${i ? '_' + i : ''}`,
                      value: 'Other',
                  },
              ]
            : []),
    ];
};

export const createDynamicBootstrap = (quoteResponse: Outlist[], parameterStore: ParameterStore) => {
    return [
        {key: 'Case_Agent_WritingState', value: parameterStore.approval.state.value},
        {key: 'Case_Agent_WritingCity', value: parameterStore.approval.addressCity.value},

        // possibly to remove this set
        {key: 'Case_Applicant_PrequalCancer', value: 'No'},
        {key: 'Case_Applicant_PrequalDiabetes', value: 'No'},
        {key: 'Case_Applicant_PrequalCoronary', value: 'No'},
        {key: 'Case_Applicant_PrequalHeart', value: 'No'},
        {key: 'Case_Applicant_PrequalCongestive', value: 'No'},
        {key: 'Case_Applicant_PrequalDefibrillatorICD', value: 'No'},
        {key: 'Case_Applicant_PrequalDiagnosed', value: 'None of the above'},
        // {key: 'Case_Lifestyle_8', value: 'No'},

        {
            key: 'Case_Applicant_PhoneNumber',
            value: parameterStore.approval.phoneNumber.value.replace(/\D/g, '').slice(-10),
        },
        {key: 'Case_Applicant_Height', value: parameterStore.prequal.height.value},
        {key: 'Case_Applicant_Weight', value: parameterStore.prequal.weight.value},

        {key: 'Case_Applicant_FirstName', value: parameterStore.approval.firstName.value},
        {key: 'Case_Applicant_MiddleName', value: ''},
        {key: 'Case_Applicant_LastName', value: parameterStore.approval.lastName.value},
        {key: 'Case_Applicant_Gender', value: parameterStore.approval.gender.value},
        {
            key: 'Case_Applicant_DOB',
            value: `${parameterStore.approval.bmonth.value}/${parameterStore.approval.bday.value}/${parameterStore.approval.byear.value}`,
        },
        {key: 'Case_Applicant_SSN', value: parameterStore.approval.socialSecurityNumber.value},
        {
            key: 'Case_Applicant_MaritalStatus',
            value: parameterStore.approval.maritalStatus.value == 'Married' ? 'Married' : 'Single',
        },

        // {key: 'Case_Applicant_BirthCountry', value: parameterStore.approval.birthCountry.value}, //'United States of America'},
        // {key: 'Case_Applicant_BirthState', value: parameterStore.approval.birthState.value},
        {
            key: 'Case_Applicant_Citizenship',
            value: mapParamStoreValueToAgenium(
                'Case_Applicant_Citizenship',
                parameterStore.approval.usCitizenship.value,
            ),
        },
        {
            key: 'Case_Applicant_DriversLicense',
            value: parameterStore.approval.validDriversLicense.value == 'Yes' ? 'Yes' : 'No',
        },
        {
            key: 'Case_Applicant_DriversLicenseState',
            value:
                parameterStore.approval.validDriversLicense.value === 'Yes'
                    ? parameterStore.approval.driversLicenseState.value
                    : '',
        },
        {
            key: 'Case_Applicant_DriversLicenseNum',
            value:
                parameterStore.approval.validDriversLicense.value === 'Yes'
                    ? parameterStore.approval.driversLicenseNumber.value
                    : '',
        },
        {key: 'Case_Applicant_EmailAddress', value: parameterStore.approval.emailAddress.value},
        {key: 'Case_Documents_MedicalAuth_APIConsent', value: '1234567'}, //???????
        {
            key: 'Case_Applicant_IndexedLifeFaceAmount',
            value: '' + quoteResponse.find((qr) => qr.name === 'Initial death benefit')?.value,
        },
        {
            key: 'Case_Applicant_IndexedLifeOriginalQuote',
            value: '' + quoteResponse.find((qr) => qr.name === 'Initial monthly premium')?.value,
        },
        {
            key: 'Case_Applicant_DeathBenefit',
            value: mapParamStoreValueToAgenium(
                'Case_Applicant_DeathBenefit',
                parameterStore.quote.prosperDeathBenefitOption.value,
            ),
        },
        {
            key: 'Case_Applicant_DeathBenefitQualification',
            value: mapParamStoreValueToAgenium(
                'Case_Applicant_DeathBenefitQualification',
                parameterStore.quote.prosperDefinitionOfLifeInsurance.value,
            ),
        },
        {
            key: 'Case_Applicant_AnnualEarnedIncome',
            value: _.isString(parameterStore.prequal.incomeHouseholdText.value)
                ? parameterStore.prequal.incomeHouseholdText.value.replace(/\D/g, '')
                : _.isNumber(parameterStore.prequal.incomeHouseholdText.value)
                  ? '' + parameterStore.prequal.incomeHouseholdText.value
                  : '',
        },
        {key: 'Case_Applicant_ElectronicDelivery', value: 'Yes'},
        {key: 'Case_Applicant_LifeSettlement', value: parameterStore.instantIulMultiQuestion ? 'Yes' : 'No'},
        {key: 'Case_Applicant_LifeIncentive', value: parameterStore.instantIulMultiQuestion ? 'Yes' : 'No'},
        {key: 'Case_Applicant_LifeContract', value: parameterStore.instantIulMultiQuestion ? 'Yes' : 'No'},
        // BENEFICIARIES
        {key: 'Case_BeneDetails_PrimeBlock', value: parameterStore.approval.numberOfBenes.value || 0},
        ...Array(parameterStore.approval.numberOfBenes.value || 0)
            .fill(null)
            .map((t, i) => createBeneficiaryKeys(parameterStore, i))
            .flat(),
        {key: 'Case_BeneDetails_ContBene', value: parameterStore.approval.contNumberOfBenes.value ? 'Yes' : 'No'},
        {key: 'Case_BeneDetails_ContinBlock', value: parameterStore.approval.contNumberOfBenes.value || 0},
        ...Array(parameterStore.approval.contNumberOfBenes.value || 0)
            .fill(null)
            .map((t, i) => createBeneficiaryKeys(parameterStore, i, true))
            .flat(),
        // EMPLOYER
        ...(!EMPLOYER_OPTIONAL_OCCUPATIONS.includes(parameterStore.approval.occupation.value)
            ? [{key: 'Case_Applicant_Occupation', value: parameterStore.approval.occupation.value}]
            : []),
        {
            key: 'Case_Applicant_Employer',
            value: parameterStore.approval.nameOfEmployer.value,
        },
        // EXISTING_LIFE_INSURANCE
        {
            key: 'Case_Applicant_ExInsurance',
            value: parameterStore.approval.existingCoverage.value === true ? 'Yes' : 'No',
        },
        ...(parameterStore.approval.existingCoverage.value === true
            ? [
                  {
                      key: 'Case_OtherInsurance_CompanyName',
                      value: parameterStore.approval.existingCarrier.value,
                  },
                  {key: 'Case_OtherInsurance_FaceAmount', value: parameterStore.approval.existingCoverageAmount.value},
                  {
                      key: 'Case_OtherInsurance_Replacement',
                      value: parameterStore.approval.replacingExistingPolicy.value === true ? 'Yes' : 'No',
                  },
                  {
                      key: 'Case_OtherInsurance_1035Exchange',
                      value: parameterStore.approval.replacingExistingPolicy.value === true ? 'Yes' : 'No',
                  },
                  {
                      key: 'Case_OtherInsurance_ExInsurancetype',
                      value:
                          parameterStore.approval.existingPolicyType.value === 'Annuities'
                              ? 'Annuties' //TODO: temp, until the value is fixed on Agenium side
                              : 'Life insurance policies',
                  },
                  {key: 'Case_OtherInsurance_CoSeq', value: 'Company 1'},
                  ...(parameterStore.approval.existingCoverageAmount1.value
                      ? [
                            {
                                key: 'Case_OtherInsurance_CompanyName_1',
                                value: parameterStore.approval.existingCarrier1.value,
                            },
                            {
                                key: 'Case_OtherInsurance_FaceAmount_1',
                                value: parameterStore.approval.existingCoverageAmount1.value,
                            },
                            {
                                key: 'Case_OtherInsurance_Replacement_1',
                                value: parameterStore.approval.replacingExistingPolicy1.value === true ? 'Yes' : 'No',
                            },
                            {
                                key: 'Case_OtherInsurance_1035Exchange_1',
                                value: parameterStore.approval.replacingExistingPolicy1.value === true ? 'Yes' : 'No',
                            },
                            {
                                key: 'Case_OtherInsurance_ExInsurancetype_1',
                                value:
                                    parameterStore.approval.existingPolicyType1.value === 'Annuities'
                                        ? 'Annuties' //TODO: temp, until the value is fixed on Agenium side
                                        : 'Life insurance policies',
                            },
                            {key: 'Case_OtherInsurance_CoSeq_1', value: 'Company 2'},
                        ]
                      : []),
                  ...(parameterStore.approval.existingCoverageAmount2.value
                      ? [
                            {
                                key: 'Case_OtherInsurance_CompanyName_2',
                                value: parameterStore.approval.existingCarrier2.value,
                            },
                            {
                                key: 'Case_OtherInsurance_FaceAmount_2',
                                value: parameterStore.approval.existingCoverageAmount2.value,
                            },
                            {
                                key: 'Case_OtherInsurance_Replacement_2',
                                value: parameterStore.approval.replacingExistingPolicy2.value === true ? 'Yes' : 'No',
                            },
                            {
                                key: 'Case_OtherInsurance_1035Exchange_2',
                                value: parameterStore.approval.replacingExistingPolicy2.value === true ? 'Yes' : 'No',
                            },
                            {
                                key: 'Case_OtherInsurance_ExInsurancetype_2',
                                value:
                                    parameterStore.approval.existingPolicyType2.value === 'Annuities'
                                        ? 'Annuties' //TODO: temp, until the value is fixed on Agenium side
                                        : 'Life insurance policies',
                            },
                            {key: 'Case_OtherInsurance_CoSeq_2', value: 'Company 3'},
                        ]
                      : []),
                  {key: 'Case_Applicant_Replacement', value: 'No'},
                  {
                      key: 'Case_OtherInsurance_Block',
                      value: `${(parameterStore.approval.existingCoverageAmount.value ? 1 : 0) + (parameterStore.approval.existingCoverageAmount1.value ? 1 : 0) + (parameterStore.approval.existingCoverageAmount2.value ? 1 : 0)}`,
                  },
                  {
                      key: 'Case_OtherInsurance_BlockCompany',
                      value: `${(parameterStore.approval.existingCoverageAmount.value ? 1 : 0) + (parameterStore.approval.existingCoverageAmount1.value ? 1 : 0) + (parameterStore.approval.existingCoverageAmount2.value ? 1 : 0)}`,
                  },
              ]
            : []),
        // LAST_DOCTOR_VISIT
        {
            key: 'Case_Medical_2',
            value: parameterStore.approval.lastDoctorVisitLastVisited.value ? 'Yes' : 'No',
        },
        ...(parameterStore.approval.lastDoctorVisitLastVisited.value
            ? [
                  {
                      key: 'Case_Medical_2ConsultDateMonth',
                      value: parameterStore.approval.lastDoctorVisitLastVisited.value.split('/')[0],
                  },
                  {
                      key: 'Case_Medical_2ConsultDateYear',
                      value: parameterStore.approval.lastDoctorVisitLastVisited.value.split('/')[2],
                  },
                  {
                      key: 'Case_Medical_2ConsultDateMonthYear',
                      value:
                          parameterStore.approval.lastDoctorVisitLastVisited.value.split('/')[0] +
                          '/' +
                          parameterStore.approval.lastDoctorVisitLastVisited.value.split('/')[2],
                  },
                  {
                      key: 'Case_Medical_2ConsultFirstName',
                      value: parameterStore.approval.specialistName.value.includes('  ')
                          ? parameterStore.approval.specialistName.value.substring(
                                0,
                                parameterStore.approval.specialistName.value.indexOf('  '),
                            )
                          : '  ',
                  },
                  {
                      key: 'Case_Medical_2ConsultLastName',
                      value: parameterStore.approval.specialistName.value.substring(
                          parameterStore.approval.specialistName.value.indexOf('  ') +
                              (parameterStore.approval.specialistName.value.includes('  ') ? 2 : 1),
                      ),
                  },
                  {
                      key: 'Case_Medical_2ConsultStreetAddress',
                      value: parameterStore.approval.lastDoctorVisitStreet.value,
                  },
                  {
                      key: 'Case_Medical_2ConsultCity',
                      value: parameterStore.approval.lastDoctorVisitCity.value,
                  },
                  {key: 'Case_Medical_2ConsultState', value: parameterStore.approval.lastDoctorVisitState.value},
                  {key: 'Case_Medical_2ConsultZipCode', value: parameterStore.approval.lastDoctorVisitZip.value},
                  {
                      key: 'Case_Medical_2Reason',
                      value: parameterStore.approval.lastDoctorVisitReason.value,
                  },
                  ...(parameterStore.approval.lastDoctorVisitReason.value === 'Routine annual wellness check up'
                      ? [
                            {
                                key: 'Case_Medical_2RoutineCheckUp',
                                value: parameterStore.approval.lastDoctorVisitReasonAddnTests.value ? 'Yes' : 'No',
                            },
                            ...(parameterStore.approval.lastDoctorVisitReasonAddnTests.value
                                ? [
                                      {
                                          key: 'Case_Medical_2ReasonAddnDetails',
                                          value: '   ',
                                      },
                                  ]
                                : []),
                        ]
                      : []),
                  ...(parameterStore.approval.lastDoctorVisitReason.value === 'Follow up on labwork or testing'
                      ? [
                            {
                                key: 'Case_Medical_2LabworkFollowUp',
                                value: parameterStore.approval.lastDoctorVisitReasonAddnTests.value ? 'Yes' : 'No',
                            },
                            ...(parameterStore.approval.lastDoctorVisitReasonAddnTests.value
                                ? [
                                      {
                                          key: 'Case_Medical_2ReasonAddnDetails2',
                                          value:
                                              parameterStore.approval.lastDoctorVisitReasonAddnTestsDetails.value ||
                                              '   ',
                                      },
                                  ]
                                : []),
                        ]
                      : []),
              ]
            : []),
        // BIRTHPLACE
        {key: 'Case_Applicant_BirthCountry', value: parameterStore.approval.birthCountry.value},
        ...(parameterStore.approval.birthCountry.value === 'United States of America'
            ? [{key: 'Case_Applicant_BirthState', value: parameterStore.approval.birthState.value}]
            : []),
        // ADDRESS
        {key: 'Case_Applicant_AddressLine1', value: parameterStore.approval.address.value.split('  Unit ')[0]},
        {
            key: 'Case_Applicant_AddressLine2',
            value: parameterStore.approval.address.value.split('  Unit ')[1]
                ? `Unit ${parameterStore.approval.address.value.split('  Unit ')[1]}`
                : '',
        },
        {key: 'Case_Applicant_City', value: parameterStore.approval.addressCity.value},
        {key: 'Case_Applicant_State', value: parameterStore.approval.state.value},
        {key: 'Case_Applicant_PostalCode', value: parameterStore.approval.addressZipCode.value},
    ];
};
